import { useCallback, useRef } from 'react';
import API from '@/utils/api';
import {
  openDatabase,
  getAllFromStore,
  saveToStore,
  deleteFromStore,
  clearStore,
  syncWithServer,
  isIndexedDBAvailable,
  STORES,
} from '@/services/indexedDBService';
import { addToSyncQueue, getSyncQueue, removeFromSyncQueue } from '@/services/offlineDataService';

/**
 * useOfflineFirst - Hook for offline-first data fetching with IndexedDB
 * 
 * Features:
 * - Fetches from IndexedDB first (instant UI)
 * - Then fetches from server and updates cache
 * - Queues writes for offline sync
 * - Handles conflicts with server data
 */
const useOfflineFirst = (storeName) => {
  const isFetching = useRef(false);

  /**
   * Fetch data with offline-first strategy
   * 1. Return cached data immediately
   * 2. Fetch from server in background
   * 3. Update cache and return fresh data
   */
  const fetchWithCache = useCallback(async (endpoint, options = {}) => {
    const { forceRefresh = false, idKey = 'id' } = options;
    
    // Check if IndexedDB is available
    if (!isIndexedDBAvailable()) {
      // Fall back to direct API call
      const response = await API.get(endpoint);
      return { data: response.data, fromCache: false };
    }

    try {
      await openDatabase();
      
      // Step 1: Get cached data first (for instant UI)
      let cachedData = [];
      if (!forceRefresh) {
        try {
          cachedData = await getAllFromStore(storeName);
        } catch (e) {
          console.warn('Failed to read from IndexedDB cache:', e);
        }
      }

      // Step 2: Fetch from server
      if (navigator.onLine && !isFetching.current) {
        isFetching.current = true;
        
        try {
          const response = await API.get(endpoint);
          const serverData = Array.isArray(response.data) ? response.data : [response.data];
          
          // Step 3: Sync with local cache
          if (serverData.length > 0) {
            await syncWithServer(storeName, serverData, idKey);
          }
          
          isFetching.current = false;
          return { data: response.data, fromCache: false, synced: true };
        } catch (error) {
          isFetching.current = false;
          
          // If network fails, return cached data
          if (cachedData.length > 0) {
            console.log(`Network failed, using ${cachedData.length} cached items for ${storeName}`);
            return { data: cachedData, fromCache: true, error };
          }
          throw error;
        }
      }

      // Offline or already fetching - return cached data
      if (cachedData.length > 0) {
        return { data: cachedData, fromCache: true };
      }

      // No cache and offline
      throw new Error('No cached data available and device is offline');
    } catch (error) {
      console.error(`useOfflineFirst error for ${storeName}:`, error);
      throw error;
    }
  }, [storeName]);

  /**
   * Save item with offline support
   * - Saves to IndexedDB immediately
   * - Queues API call for when online
   */
  const saveWithOfflineSupport = useCallback(async (endpoint, data, method = 'POST') => {
    const itemId = data.id || `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const itemWithId = { ...data, id: itemId };

    // Save to IndexedDB immediately
    if (isIndexedDBAvailable()) {
      try {
        await saveToStore(storeName, {
          ...itemWithId,
          syncStatus: navigator.onLine ? 'syncing' : 'pending',
          _localUpdatedAt: new Date().toISOString(),
        });
      } catch (e) {
        console.warn('Failed to save to IndexedDB:', e);
      }
    }

    // If online, make API call
    if (navigator.onLine) {
      try {
        let response;
        if (method === 'POST') {
          response = await API.post(endpoint, data);
        } else if (method === 'PUT') {
          response = await API.put(endpoint, data);
        } else if (method === 'PATCH') {
          response = await API.patch(endpoint, data);
        }

        // Update IndexedDB with server response
        if (isIndexedDBAvailable() && response.data) {
          await saveToStore(storeName, {
            ...response.data,
            syncStatus: 'synced',
            lastSyncedAt: new Date().toISOString(),
          });
        }

        return { data: response.data, synced: true };
      } catch (error) {
        // Queue for later sync
        addToSyncQueue({ method, endpoint, data: itemWithId });
        
        if (isIndexedDBAvailable()) {
          await saveToStore(storeName, {
            ...itemWithId,
            syncStatus: 'pending',
          });
        }
        
        return { data: itemWithId, synced: false, queued: true, error };
      }
    } else {
      // Offline - queue for sync
      addToSyncQueue({ method, endpoint, data: itemWithId });
      return { data: itemWithId, synced: false, queued: true };
    }
  }, [storeName]);

  /**
   * Delete item with offline support
   */
  const deleteWithOfflineSupport = useCallback(async (endpoint, itemId) => {
    // Mark as deleted in IndexedDB
    if (isIndexedDBAvailable()) {
      try {
        await deleteFromStore(storeName, itemId);
      } catch (e) {
        console.warn('Failed to delete from IndexedDB:', e);
      }
    }

    // If online, make API call
    if (navigator.onLine) {
      try {
        await API.delete(endpoint);
        return { synced: true };
      } catch (error) {
        // Queue for later sync
        addToSyncQueue({ method: 'DELETE', endpoint, data: { id: itemId } });
        return { synced: false, queued: true, error };
      }
    } else {
      // Queue for sync
      addToSyncQueue({ method: 'DELETE', endpoint, data: { id: itemId } });
      return { synced: false, queued: true };
    }
  }, [storeName]);

  /**
   * Sync pending changes to server
   */
  const syncPendingChanges = useCallback(async () => {
    if (!navigator.onLine) return { synced: 0, failed: 0 };

    const queue = getSyncQueue();
    let synced = 0;
    let failed = 0;

    for (const operation of queue) {
      try {
        const { method, endpoint, data } = operation;
        
        if (method === 'POST') {
          await API.post(endpoint, data);
        } else if (method === 'PUT') {
          await API.put(endpoint, data);
        } else if (method === 'PATCH') {
          await API.patch(endpoint, data);
        } else if (method === 'DELETE') {
          await API.delete(endpoint);
        }

        removeFromSyncQueue(operation.id);
        synced++;
      } catch (error) {
        console.error('Failed to sync operation:', error);
        failed++;
      }
    }

    return { synced, failed };
  }, []);

  /**
   * Clear the local cache
   */
  const clearCache = useCallback(async () => {
    if (isIndexedDBAvailable()) {
      await clearStore(storeName);
    }
  }, [storeName]);

  return {
    fetchWithCache,
    saveWithOfflineSupport,
    deleteWithOfflineSupport,
    syncPendingChanges,
    clearCache,
    isOfflineAvailable: isIndexedDBAvailable(),
  };
};

export default useOfflineFirst;

// Export store names for convenience
export { STORES };
